import { useCallback, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useDropzone } from "react-dropzone";
import type { FileRejection } from "react-dropzone";
import { useSnackbar } from "notistack";
import {
  Button,
  FileViewer,
  Modal,
  styled,
} from "@monolith-forensics/monolith-ui";
import Loader from "@/components/Loader";
import SimpleCamera from "@/components/Camera/SimpleCamera";
import { MONOLITH_PERMISSIONS } from "@/constants.js";
import { usePermissions } from "@/hooks/usePermissions";
import type { File } from "@/Features/Files/files.types";
import { PhotoGrid } from "@/Features/Files/components/PhotoGrid";
import { mergePhotoEntries } from "@/Features/Files/components/PhotoGrid/mergePhotoEntries";
import { useUploadQueue } from "@/Features/Files/Hooks/useUploadQueue";
import { dataUrlsToFiles } from "@/Features/Files/utils/dataUrlsToFiles";
import {
  StoragePhotosAPI,
  applyUploadedStoragePhoto,
  storagePhotosQueryKeys,
  useGetStoragePhotos,
} from "@/Features/StoragePhotos";
import type { StoragePhoto } from "@/Features/StoragePhotos";
import type { StorageItem } from "@/Features/Storage/Tables/types";
import { DeleteStoragePhotoModal } from "./DeleteStoragePhotoModal";
import { CameraIcon, UploadIcon } from "lucide-react";

// Matches the API allowlist. TIFF is excluded: Chrome and Firefox do not
// render it in an <img>.
const ALLOWED_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "avif"];
const MAX_PHOTO_SIZE = 50 * 1024 * 1024;
// Minimum gap between invalidations triggered by broken <img> links, so a
// link that stays broken cannot refetch in a loop.
const IMAGE_ERROR_REFETCH_INTERVAL = 30 * 1000;

const StyledContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;

  .photos-header {
    display: flex;
    align-items: center;
    gap: 10px;
  }
`;

// Same transition as the CaseFilesTable drop overlay.
const StyledOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  background-color: ${({ theme }) => theme.palette.background.default}cc;

  opacity: 0;
  visibility: hidden;
  pointer-events: none;

  transition: opacity 0.2s ease-in-out, visibility 0.2s ease-in-out,
    outline 0.2s ease-in-out;

  border-radius: 2px;
  outline: 2px solid transparent;

  &.dragging-over {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    outline: 2px solid ${({ theme }) => theme.palette.primary.main};
  }
`;

const preventDefault = (e: React.DragEvent) => e.preventDefault();

const extensionOf = (name: string) =>
  name.split(".").pop()?.toLowerCase() ?? "";

const rejectionReason = ({ file, errors }: FileRejection) => {
  const code = errors[0]?.code;
  if (code === "file-too-large") {
    return `${file.name}: larger than ${MAX_PHOTO_SIZE / 1024 / 1024} MB`;
  }
  if (code === "unsupported-extension") {
    return `${file.name}: unsupported photo type (use ${ALLOWED_EXTENSIONS.join(
      ", "
    )})`;
  }
  return `${file.name}: not a supported image`;
};

const validateExtension = (file: globalThis.File) =>
  ALLOWED_EXTENSIONS.includes(extensionOf(file.name))
    ? null
    : { code: "unsupported-extension", message: "Unsupported photo type" };

export const StoragePhotosContent: React.FC<{
  storageInfo: StorageItem & { storage_id: number; uuid: string };
}> = ({ storageInfo }) => {
  const { storage_id, uuid } = storageInfo;
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();
  const canUpload = hasPermission(MONOLITH_PERMISSIONS.STORAGE_UPDATE);
  const canDelete = canUpload;

  const [showCamera, setShowCamera] = useState(false);
  const [viewerFile, setViewerFile] = useState<File | null>(null);
  const [showViewer, setShowViewer] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState<File | null>(null);
  const lastImageRefetch = useRef(0);

  const { data, isLoading, isError, error } = useGetStoragePhotos({
    storage_id,
    uuid,
  });

  const api = useMemo(() => StoragePhotosAPI.uploaderApi(uuid), [uuid]);

  const { uploads, enqueue, dismiss } = useUploadQueue({
    api,
    concurrency: 3,
    hashFile: true,
    onUploaded: (_uploadUuid, result) => {
      const photo = (result as { data?: StoragePhoto } | undefined)?.data;
      void applyUploadedStoragePhoto(queryClient, storage_id, photo);
    },
    // No snackbar: the axios interceptor already toasts HTTP failures, and the
    // failed tile shows the error message.
  });

  const handleDrop = useCallback(
    (accepted: globalThis.File[], rejected: FileRejection[]) => {
      if (rejected.length > 0) {
        const [first, ...others] = rejected;
        enqueueSnackbar(
          others.length > 0
            ? `${rejected.length} files were not uploaded. ${rejectionReason(
                first
              )} (and ${others.length} more)`
            : rejectionReason(first),
          { variant: "error" }
        );
      }
      if (accepted.length > 0) enqueue(accepted);
    },
    [enqueue, enqueueSnackbar]
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: handleDrop,
    accept: { "image/*": [] },
    validator: validateExtension,
    maxSize: MAX_PHOTO_SIZE,
    multiple: true,
    noClick: true,
    noKeyboard: true,
    disabled: !canUpload,
  });

  const handleCapture = (urls: string[]) => {
    setShowCamera(false);
    dataUrlsToFiles(urls, "storage-photo")
      .then((files) => enqueue(files))
      .catch(() =>
        enqueueSnackbar("Failed to process photo.", { variant: "error" })
      );
  };

  // A signed link past its lifetime 403s in the <img>; refetch for fresh ones.
  const handleImageError = useCallback(() => {
    const now = Date.now();
    if (now - lastImageRefetch.current < IMAGE_ERROR_REFETCH_INTERVAL) return;
    lastImageRefetch.current = now;
    queryClient.invalidateQueries({
      queryKey: storagePhotosQueryKeys.list(storage_id),
    });
  }, [queryClient, storage_id]);

  const handleView = (file: File) => {
    setViewerFile(file);
    setShowViewer(true);
  };

  const entries = useMemo(
    () => mergePhotoEntries(data ?? [], Object.values(uploads)),
    [data, uploads]
  );

  const renderBody = () => {
    if (isLoading) return <Loader />;
    // The axios interceptor already toasts the failure; show it inline only.
    if (isError && !data) {
      return (
        <div style={{ marginTop: 20 }}>
          Unable to load photos
          {error instanceof Error && error.message ? `: ${error.message}` : "."}
        </div>
      );
    }
    return (
      <PhotoGrid
        entries={entries}
        onView={handleView}
        onDelete={canDelete ? setPhotoToDelete : undefined}
        onDismiss={dismiss}
        onImageError={handleImageError}
        emptyMessage="No storage photos"
      />
    );
  };

  return (
    <StyledContainer
      {...getRootProps()}
      {...(!canUpload && {
        // A disabled dropzone drops its drag handlers, so the browser would
        // open a dropped image and navigate away. Make the drop inert.
        onDragOver: preventDefault,
        onDrop: preventDefault,
      })}
    >
      <input {...getInputProps()} />
      <StyledOverlay className={isDragActive ? "dragging-over" : undefined}>
        <div style={{ fontWeight: 600 }}>Drop photos to upload</div>
      </StyledOverlay>
      {canUpload && (
        <div className="photos-header">
          <Button
            size="xs"
            variant="contained"
            color="primary"
            onClick={open}
            leftSection={<UploadIcon size={14} />}
          >
            Upload Photos
          </Button>
          <Button
            size="xs"
            variant="contained"
            color="primary"
            leftSection={<CameraIcon size={14} />}
            onClick={() => setShowCamera(true)}
          >
            Take Photos
          </Button>
        </div>
      )}
      {renderBody()}
      {showCamera && (
        <Modal open={showCamera} onClose={() => setShowCamera(false)}>
          <Modal.Title>Take Photos</Modal.Title>
          <div style={{ width: "50vw" }}>
            <SimpleCamera
              onCapture={handleCapture}
              onClose={() => setShowCamera(false)}
            />
          </div>
        </Modal>
      )}
      {photoToDelete && (
        <DeleteStoragePhotoModal
          show={!!photoToDelete}
          storageId={storage_id}
          storageUuid={uuid}
          photo={photoToDelete}
          onClose={() => setPhotoToDelete(null)}
          onSubmit={() => setPhotoToDelete(null)}
        />
      )}
      <FileViewer
        file={{
          name: viewerFile?.name ?? "",
          url: viewerFile?.link,
          ext: extensionOf(viewerFile?.name ?? ""),
          size: viewerFile?.size,
          type: extensionOf(viewerFile?.name ?? ""),
        }}
        open={showViewer}
        onClose={() => setShowViewer(false)}
      />
    </StyledContainer>
  );
};

export default StoragePhotosContent;
